import { useState } from 'react'
import clsx from 'clsx'
import { ArrowUp, AudioLines, Loader2, SlidersHorizontal, Zap } from 'lucide-react'

import type { AIModel } from '@/api/types'
import {
  SEED_AUDIO_FORMATS,
  SEED_AUDIO_LANGUAGES,
  SEED_AUDIO_PROMPT_MAX,
  SEED_AUDIO_SAMPLE_RATES,
  resolveSeedAudioParams,
} from '@/features/studio/canvas/audio-params'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'
import { buildCanvasModelOptions } from '@/features/studio/canvas/model-options'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'

type CanvasT = (key: string, opts?: Record<string, unknown>) => string

export function SeedAudioPanel({
  modelId,
  models,
  params,
  promptLength,
  cost,
  runDisabled,
  runRunning,
  onModelChange,
  onParamsChange,
  onRun,
  t,
}: {
  modelId: string
  models: AIModel[]
  params?: Record<string, string | number>
  promptLength: number
  cost: number
  runDisabled?: boolean
  runRunning?: boolean
  onModelChange: (modelId: string) => void
  onParamsChange: (patch: Record<string, string | number>) => void
  onRun: () => void
  t: CanvasT
}) {
  const resolved = resolveSeedAudioParams(params)
  const [presetOpen, setPresetOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const modelOptions = buildCanvasModelOptions(models, t)
  const langLabel = t(
    resolved.language === 'en' ? 'canvas.params.langEn' : 'canvas.params.langZh',
  )
  const rateLabel = SEED_AUDIO_SAMPLE_RATES.find((r) => r.value === resolved.sample_rate)?.label ?? '24k'
  const summary = `${langLabel} · ${rateLabel} · ${resolved.format}`
  const used = Math.min(promptLength, SEED_AUDIO_PROMPT_MAX)

  return (
    <div className="cn-gen-controls cn-seed-audio nodrag nopan nowheel">
      <div className="cn-seed-audio__bar">
        <span className="cn-seed-audio__model-icon" aria-hidden>
          <AudioLines className="h-3.5 w-3.5" />
        </span>
        <ProtoDropdown
          compact
          portal
          className="cn-seed-audio__model"
          value={modelId}
          onValueChange={onModelChange}
          options={modelOptions}
          disabled={!modelOptions.length}
          placeholder={t('canvas.section.model')}
        />

        <Popover open={presetOpen} onOpenChange={setPresetOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={clsx('cn-seed-audio__pill', presetOpen && 'cn-seed-audio__pill--open')}
              aria-label={t('canvas.seedAudio.preset')}
            >
              {summary}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="top"
            className="cn-seed-audio__pop nodrag nopan nowheel"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ChipGroup
              label={t('canvas.params.language')}
              options={SEED_AUDIO_LANGUAGES.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
              value={resolved.language}
              onChange={(language) => onParamsChange({ language })}
            />
            <ChipGroup
              label={t('canvas.params.sampleRate')}
              options={SEED_AUDIO_SAMPLE_RATES.map((o) => ({
                value: String(o.value),
                label: o.label,
              }))}
              value={String(resolved.sample_rate)}
              onChange={(v) => onParamsChange({ sample_rate: Number(v) })}
            />
            <ChipGroup
              label={t('canvas.params.outputFormat')}
              options={SEED_AUDIO_FORMATS.map((format) => ({ value: format, label: format }))}
              value={resolved.format}
              onChange={(format) => onParamsChange({ format })}
            />
          </PopoverContent>
        </Popover>

        <button
          type="button"
          className={clsx('cn-seed-audio__icon-btn', advancedOpen && 'cn-seed-audio__icon-btn--on')}
          aria-expanded={advancedOpen}
          aria-label={t('canvas.seedAudio.advanced')}
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>

        <span className="cn-seed-audio__count" title={t('canvas.seedAudio.promptLimitHint')}>
          {used}/{SEED_AUDIO_PROMPT_MAX}
        </span>
        <span className="cn-seed-audio__cost" title={t('canvas.modelCost', { cost })}>
          <Zap className="h-3 w-3" />
          {cost}
        </span>
        <button
          type="button"
          className="cn-seed-audio__run"
          disabled={runDisabled}
          onClick={onRun}
          aria-label={t('canvas.run')}
        >
          {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-3.5 w-3.5" />}
        </button>
      </div>

      {advancedOpen ? (
        <div className="cn-seed-audio__advanced">
          <div className="cn-seed-audio__advanced-title">{t('canvas.seedAudio.advanced')}</div>
          <SliderRow
            label={t('canvas.params.speed')}
            min={0.5}
            max={2}
            step={0.01}
            value={resolved.speed}
            display={resolved.speed.toFixed(2)}
            onChange={(speed) => onParamsChange({ speed })}
          />
          <SliderRow
            label={t('canvas.params.pitchTone')}
            min={-12}
            max={12}
            step={1}
            value={resolved.pitch}
            display={String(Math.round(resolved.pitch))}
            onChange={(pitch) => onParamsChange({ pitch })}
          />
          <SliderRow
            label={t('canvas.params.volume')}
            min={0.5}
            max={2}
            step={0.1}
            value={resolved.volume}
            display={resolved.volume.toFixed(1)}
            onChange={(volume) => onParamsChange({ volume })}
          />
        </div>
      ) : null}
    </div>
  )
}

function ChipGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="cn-seed-audio__group">
      <div className="cn-seed-audio__group-label">{label}</div>
      <div className="cn-seed-audio__chips">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={clsx('cn-seed-audio__chip', value === o.value && 'cn-seed-audio__chip--on')}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function SliderRow({
  label,
  min,
  max,
  step,
  value,
  display,
  onChange,
}: {
  label: string
  min: number
  max: number
  step: number
  value: number
  display: string
  onChange: (value: number) => void
}) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="cn-seed-audio__slider">
      <span className="cn-seed-audio__slider-label">{label}</span>
      <input
        type="range"
        className="cn-seed-audio__range"
        style={{ ['--seed-pct' as string]: `${pct}%` }}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      />
      <input
        type="number"
        className="cn-seed-audio__num"
        min={min}
        max={max}
        step={step}
        value={display}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (Number.isFinite(n)) onChange(n)
        }}
        aria-label={label}
      />
    </div>
  )
}
